import { InputHTMLAttributes, memo, forwardRef, ReactNode, useId } from 'react';
import './Input.css';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  /** Icon shown inline next to the label text (outside the field) */
  leftIcon?: ReactNode;
  /** Icon/button inside the right edge of the dark input card */
  rightIcon?: ReactNode;
  containerClassName?: string;
}

/**
 * AuthInput
 *
 * Layout - label + input share ONE dark surface (#020A15):
 *
 *  ┌──────────────────────────────┐
 *  │ ⊕ FULL NAME   ← label (top) │
 *  │ Arthur Pendragon ← input     │
 *  ├──────────────────────────────┤  ← border-top separator
 *  │ ✉ EMAIL ADDRESS              │
 *  │ you@company.com              │
 *  └──────────────────────────────┘
 *
 * Spec: background #020A15, border-top 1px #FFFFFF14, border-radius: 0
 */
export const Input = memo(
  forwardRef<HTMLInputElement, InputProps>(
    (
      {
        label,
        error,
        helperText,
        leftIcon,
        rightIcon,
        className = '',
        containerClassName = '',
        id,
        ...props
      },
      ref
    ) => {
      const uid = useId();
      const inputId = id ?? `input-${uid}`;

      return (
        <div className={`auth-input-container ${containerClassName}`}>

          {/* Label - sits ABOVE the dark card */}
          {label && (
            <label htmlFor={inputId} className="auth-input-label">
              {leftIcon && (
                <span style={{ display: 'flex', alignItems: 'center', opacity: 0.65 }}>
                  {leftIcon}
                </span>
              )}
              {label}
            </label>
          )}

          {/* Dark input box - label sits above this, outside it */}
          <div className="auth-input-wrapper">
            <input
              ref={ref}
              id={inputId}
              className={[
                'auth-input',
                rightIcon ? 'auth-input--has-right-icon' : '',
                className,
              ]
                .filter(Boolean)
                .join(' ')}
              aria-invalid={!!error}
              aria-describedby={
                error      ? `${inputId}-error`  :
                helperText ? `${inputId}-helper` : undefined
              }
              {...props}
            />

            {rightIcon && (
              <span className="auth-input-icon">
                {rightIcon}
              </span>
            )}
          </div>

          {error && (
            <span id={`${inputId}-error`} className="auth-input-error" role="alert">
              {error}
            </span>
          )}
          {helperText && !error && (
            <span id={`${inputId}-helper`} className="auth-input-helper">
              {helperText}
            </span>
          )}
        </div>
      );
    }
  )
);

Input.displayName = 'Input';
